/**
 * MapCanvas.jsx
 * Core SVG renderer for the synagogue seating map.
 * Shared between display templates and the editor.
 *
 * Interactive features (editor only):
 *  - onLandmarkUpdate  → landmarks become draggable + resizable
 *  - onRowUpdate       → rows become draggable (drag any seat to move the row)
 *  - onSeatClick       → fires on short click (no drag); opens seat naming
 */
import React, { useMemo, useRef, useCallback } from 'react';
import {
  getAllSeatsWithPositions,
  LANDMARK_LABELS,
  DEFAULT_SEAT_SIZE,
  SEAT_GAP,
} from './seatingMapUtils';

// ─── Landmark rendering ───────────────────────────────────────────────────────

const LandmarkShape = ({ landmark, colors }) => {
  const label = LANDMARK_LABELS[landmark.type] || landmark.type;
  const isEntrance = landmark.type === 'entrance';
  const isRounded = landmark.type === 'bima' || landmark.type === 'amudTefilla';

  return (
    <g>
      <rect
        x={landmark.x}
        y={landmark.y}
        width={landmark.width}
        height={landmark.height}
        rx={isRounded ? 0.5 : 0.2}
        fill={isEntrance ? 'transparent' : colors.landmark}
        stroke={colors.landmark}
        strokeWidth={0.15}
        strokeDasharray={isEntrance ? '0.4 0.2' : 'none'}
        opacity={0.85}
      />
      <text
        x={landmark.x + landmark.width / 2}
        y={landmark.y + landmark.height / 2 + 0.35}
        textAnchor="middle"
        fontSize={Math.min(0.9, landmark.width * 0.15)}
        fill={isEntrance ? colors.landmark : '#fff'}
        fontFamily="Arial, sans-serif"
        fontWeight="bold"
        direction="rtl"
        style={{ pointerEvents: 'none' }}
      >
        {label}
      </text>
    </g>
  );
};

// ─── Seat rendering ───────────────────────────────────────────────────────────

/** Split name on every space — each word gets its own line. No truncation. */
const splitSeatName = (name) => {
  if (!name) return [];
  return name.split(' ').filter(Boolean);
};

const SeatRect = ({ seat, seatSize, fill, fontSize, lines, showName }) => {
  const isOccupied = Boolean(seat.occupant);
  const cx = seat.x + seatSize / 2;
  const n = lines.length;
  const lineHeight = fontSize * 1.15;
  // Vertical center: offset so the block of N lines is centred in the seat
  const blockHeight = (n - 1) * lineHeight;
  const firstLineY = seat.y + seatSize / 2 + fontSize * 0.35 - blockHeight / 2;

  return (
    <>
      <rect
        x={seat.x}
        y={seat.y}
        width={seatSize}
        height={seatSize}
        rx={seatSize * 0.11}
        fill={fill}
        stroke={isOccupied ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)'}
        strokeWidth={0.05}
      />
      {showName && n > 0 && (
        <text
          textAnchor="middle"
          fontSize={fontSize}
          fill="#fff"
          fontFamily="Arial, sans-serif"
          direction="rtl"
          style={{ pointerEvents: 'none' }}
        >
          {lines.map((line, i) => (
            <tspan key={i} x={cx} y={firstLineY + i * lineHeight}>
              {line}
            </tspan>
          ))}
        </text>
      )}
    </>
  );
};

// ─── Main MapCanvas ───────────────────────────────────────────────────────────

const DRAG_THRESHOLD = 0.3; // SVG units before treating as a drag

const MapCanvas = ({
  mapData,
  onSeatClick,
  onLandmarkUpdate,
  onRowUpdate,
  highlightedRowId,
  selectedSeatId,
  showNames = true,
  className,
  style,
}) => {
  const { width, height, landmarks, rows, seatSize = DEFAULT_SEAT_SIZE } = mapData;
  // Ensure empty seats use green even on old data with gray default
  const colors = useMemo(() => {
    const c = mapData.colors || {};
    return { occupied: '#1e3a5f', empty: '#86efac', landmark: '#8a6d08', ...c,
      ...(c.empty === '#e5e7eb' ? { empty: '#86efac' } : {}) };
  }, [mapData.colors]);
  const svgRef = useRef(null);
  const dragRef = useRef(null);      // landmark drag
  const rowDragRef = useRef(null);   // row drag (+ click detection)

  const scale = seatSize / DEFAULT_SEAT_SIZE;
  const seatGap = SEAT_GAP * scale;

  // ─── Layout ─────────────────────────────────────────────────────────

  const allSeats = useMemo(() => getAllSeatsWithPositions(mapData), [mapData]);

  const visibleLandmarks = useMemo(
    () => (landmarks || []).filter(lm => lm.visible !== false),
    [landmarks],
  );

  const fontSize = Math.min(seatSize * 0.3, 0.3);

  // ─── SVG coord helper ───────────────────────────────────────────────

  const toSvgCoords = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    return pt.matrixTransform(svg.getScreenCTM().inverse());
  }, []);

  // ─── Landmark drag / resize ─────────────────────────────────────────

  const handleLandmarkPointerDown = useCallback((e, landmarkId, type) => {
    e.preventDefault();
    e.stopPropagation();
    const pt = toSvgCoords(e);
    if (!pt) return;
    const lm = (landmarks || []).find(l => l.id === landmarkId);
    if (!lm) return;
    dragRef.current = {
      id: landmarkId, type,
      startX: pt.x, startY: pt.y,
      origX: lm.x, origY: lm.y, origW: lm.width, origH: lm.height,
    };
    e.target.setPointerCapture?.(e.pointerId);
  }, [landmarks, toSvgCoords]);

  // ─── Row drag (pointer down on seat area) ──────────────────────────

  const handleRowPointerDown = useCallback((e, rowId) => {
    if (!onRowUpdate) return;
    e.preventDefault();
    e.stopPropagation();
    const pt = toSvgCoords(e);
    if (!pt) return;
    const row = (rows || []).find(r => r.id === rowId);
    if (!row) return;
    rowDragRef.current = {
      rowId,
      startX: pt.x, startY: pt.y,
      origX: row.x, origY: row.y,
      isDragging: false,
    };
    e.target.setPointerCapture?.(e.pointerId);
  }, [onRowUpdate, rows, toSvgCoords]);

  // ─── Unified pointer move ──────────────────────────────────────────

  const handlePointerMove = useCallback((e) => {
    const pt = toSvgCoords(e);
    if (!pt) return;

    // Landmark drag
    if (dragRef.current && onLandmarkUpdate) {
      const d = dragRef.current;
      const dx = pt.x - d.startX;
      const dy = pt.y - d.startY;
      if (d.type === 'move') {
        onLandmarkUpdate(d.id, {
          x: Math.round(Math.max(0, Math.min(width - d.origW, d.origX + dx)) * 2) / 2,
          y: Math.round(Math.max(0, Math.min(height - d.origH, d.origY + dy)) * 2) / 2,
        });
      } else {
        onLandmarkUpdate(d.id, {
          width: Math.round(Math.max(1, d.origW + dx) * 2) / 2,
          height: Math.round(Math.max(0.5, d.origH + dy) * 2) / 2,
        });
      }
      return;
    }

    // Row drag
    if (rowDragRef.current && onRowUpdate) {
      const d = rowDragRef.current;
      const dx = pt.x - d.startX;
      const dy = pt.y - d.startY;
      if (!d.isDragging) {
        if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
        d.isDragging = true;
      }
      onRowUpdate(d.rowId, {
        x: Math.round(Math.max(0, d.origX + dx) * 2) / 2,
        y: Math.round(Math.max(0, d.origY + dy) * 2) / 2,
      });
    }
  }, [onLandmarkUpdate, onRowUpdate, width, height, toSvgCoords]);

  // ─── Pointer up → commit or fire click ─────────────────────────────

  const handlePointerUp = useCallback(() => {
    // Row: short click → seat naming
    if (rowDragRef.current && !rowDragRef.current.isDragging) {
      // Find which seat was under the pointer (approximate — use the rowId)
      const { rowId, startX, startY } = rowDragRef.current;
      const row = (rows || []).find(r => r.id === rowId);
      if (row && onSeatClick) {
        const seatUnit = seatSize + seatGap;
        const idx = Math.floor((startX - row.x + seatUnit / 2) / seatUnit);
        const seat = row.seats[Math.max(0, Math.min(idx, row.seats.length - 1))];
        if (seat) onSeatClick({ ...seat, rowId });
      }
    }
    rowDragRef.current = null;
    dragRef.current = null;
  }, [rows, seatSize, seatGap, onSeatClick]);

  const hs = 0.5; // landmark resize handle size

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${width} ${height}`}
      style={{ width: '100%', height: '100%', display: 'block', ...style }}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      onPointerMove={(onLandmarkUpdate || onRowUpdate) ? handlePointerMove : undefined}
      onPointerUp={(onLandmarkUpdate || onRowUpdate) ? handlePointerUp : undefined}
      onPointerLeave={(onLandmarkUpdate || onRowUpdate) ? handlePointerUp : undefined}
    >
      {/* Background — synagogue walls */}
      <rect
        x={0} y={0}
        width={width} height={height}
        fill="transparent"
        stroke="#1e3a5f"
        strokeWidth={0.15}
        rx={0.5}
      />

      {/* Landmarks */}
      {visibleLandmarks.map(lm =>
        onLandmarkUpdate ? (
          <g key={lm.id}>
            <LandmarkShape landmark={lm} colors={colors} />
            <rect
              x={lm.x} y={lm.y} width={lm.width} height={lm.height}
              fill="transparent" style={{ cursor: 'move' }}
              onPointerDown={(e) => handleLandmarkPointerDown(e, lm.id, 'move')}
            />
            <rect
              x={lm.x + lm.width - hs} y={lm.y + lm.height - hs}
              width={hs} height={hs}
              fill="rgba(255,255,255,0.75)" stroke="#888" strokeWidth={0.05} rx={0.06}
              style={{ cursor: 'nwse-resize' }}
              onPointerDown={(e) => handleLandmarkPointerDown(e, lm.id, 'resize')}
            />
          </g>
        ) : (
          <LandmarkShape key={lm.id} landmark={lm} colors={colors} />
        ),
      )}

      {/* Row labels + highlight (editor only) */}
      {onRowUpdate && (rows || []).map((row, index) => {
        const seatUnit = seatSize + seatGap;
        const rowWidth = row.seats.length * seatUnit - seatGap;
        const isHl = highlightedRowId === row.id;
        return (
          <g key={`rl-${row.id}`}>
            {isHl && (
              <rect
                x={row.x - 0.2} y={row.y - 0.2}
                width={rowWidth + 0.4} height={seatSize + 0.4}
                fill="rgba(200,168,75,0.12)"
                stroke="#c8a84b" strokeWidth={0.1} rx={0.15}
                style={{ pointerEvents: 'none' }}
              />
            )}
            <text
              x={row.x - 0.3}
              y={row.y + seatSize / 2 + 0.13}
              textAnchor="end"
              fontSize={0.42}
              fill={isHl ? '#c8a84b' : '#ccc'}
              fontFamily="Arial, sans-serif"
              fontWeight="bold"
              style={{ pointerEvents: 'none' }}
            >
              {index + 1}
            </text>
          </g>
        );
      })}

      {/* Seats */}
      {allSeats.map(seat => {
        const isOccupied = Boolean(seat.occupant);
        const fill = selectedSeatId === seat.id
          ? (colors.selected || '#c8a84b')
          : isOccupied ? colors.occupied : colors.empty;
        const lines = showNames && isOccupied
          ? splitSeatName(seat.occupant)
          : [];

        return (
          <g
            key={seat.id}
            style={{ cursor: onRowUpdate ? 'move' : onSeatClick ? 'pointer' : 'default' }}
            onPointerDown={onRowUpdate ? (e) => handleRowPointerDown(e, seat.rowId) : undefined}
            onClick={(!onRowUpdate && onSeatClick) ? () => onSeatClick(seat) : undefined}
          >
            <SeatRect
              seat={seat}
              seatSize={seatSize}
              fill={fill}
              fontSize={fontSize}
              lines={lines}
              showName={showNames}
            />
          </g>
        );
      })}
    </svg>
  );
};

export default MapCanvas;
